import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { HubConnectionBuilder, HubConnectionState, LogLevel, type HubConnection } from "@microsoft/signalr";
import { apiBaseUrl } from "./http";

export type LiveStatus = "idle" | "connecting" | "connected" | "reconnecting" | "disconnected";
export type Unsubscribe = () => void;

/** Hub methods the server exposes on `/hubs/live` (group membership is re-joined automatically after a reconnect). */
export const HUB_METHODS = {
  joinPolicy: "JoinPolicy",
  leavePolicy: "LeavePolicy",
  joinRun: "JoinRun",
  leaveRun: "LeaveRun",
} as const;

type GroupKey = `policy:${string}` | `run:${string}`;

/**
 * SignalR client for `/hubs/live`. Authenticates with the session cookie (same origin), connects once it is enabled (signed in)
 * and something uses it, and retries forever with backoff. `subscribe` registers handlers that survive reconnects and sign-ins;
 * `joinPolicy` / `joinRun` add the connection to the policy or agent-run group so its events arrive.
 */
export class LiveClient {
  private connection: HubConnection | null = null;
  private starting: Promise<void> | null = null;
  private enabled = false;
  private stopTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly groups = new Set<GroupKey>();
  private readonly handlers = new Map<string, Set<(...args: unknown[]) => void>>();
  private readonly statusListeners = new Set<(s: LiveStatus) => void>();
  private status: LiveStatus = "idle";

  constructor(private readonly hubPath = "/hubs/live") {}

  getStatus(): LiveStatus {
    return this.status;
  }

  onStatus(listener: (s: LiveStatus) => void): Unsubscribe {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  private setStatus(s: LiveStatus) {
    this.status = s;
    for (const l of this.statusListeners) l(s);
  }

  private build(): HubConnection {
    const conn = new HubConnectionBuilder()
      .withUrl(`${apiBaseUrl()}${this.hubPath}`, { withCredentials: true })
      .withAutomaticReconnect({ nextRetryDelayInMilliseconds: (ctx) => Math.min(30_000, [0, 1000, 3000, 5000, 10_000][ctx.previousRetryCount] ?? 15_000) })
      .configureLogging(LogLevel.Warning)
      .build();
    conn.onreconnecting(() => this.setStatus("reconnecting"));
    conn.onreconnected(() => {
      this.setStatus("connected");
      for (const g of this.groups) void this.invokeJoin(conn, g);
    });
    conn.onclose(() => this.setStatus("disconnected"));
    for (const [event, set] of this.handlers) for (const h of set) conn.on(event, h);
    return conn;
  }

  private invokeJoin(conn: HubConnection, group: GroupKey) {
    const [kind, id] = group.split(":") as ["policy" | "run", string];
    return conn.invoke(kind === "policy" ? HUB_METHODS.joinPolicy : HUB_METHODS.joinRun, id).catch(() => undefined);
  }

  /**
   * Turns the channel on (signed in) or off. Handlers and groups added while it is off are kept and connect when it turns on.
   * Turning off waits a tick: React's development double-mount turns it off and straight back on, and stopping a connection
   * that is still negotiating logs an error.
   */
  setEnabled(on: boolean) {
    if (this.stopTimer) clearTimeout(this.stopTimer);
    this.stopTimer = null;
    this.enabled = on;
    if (!on) {
      this.stopTimer = setTimeout(() => {
        this.stopTimer = null;
        void this.stop();
      }, 0);
    } else if (this.groups.size || [...this.handlers.values()].some((set) => set.size)) {
      void this.connect().catch(() => undefined);
    }
  }

  async connect(): Promise<HubConnection> {
    if (!this.enabled) throw new Error("The live channel opens once the user is signed in.");
    if (!this.connection) this.connection = this.build();
    const conn = this.connection;
    if (conn.state === HubConnectionState.Connected) return conn;
    if (!this.starting && conn.state === HubConnectionState.Disconnected) {
      this.setStatus("connecting");
      this.starting = conn
        .start()
        .then(() => {
          this.setStatus("connected");
          for (const g of this.groups) void this.invokeJoin(conn, g);
        })
        .catch((e) => {
          this.setStatus("disconnected");
          throw e;
        })
        .finally(() => {
          this.starting = null;
        });
    }
    if (this.starting) await this.starting;
    return conn;
  }

  /** Registers a handler for a server event (e.g. `run.step`, `policy.updated`); returns the unsubscribe function. */
  subscribe<T = unknown>(event: string, handler: (payload: T) => void): Unsubscribe {
    const h = handler as (...args: unknown[]) => void;
    let set = this.handlers.get(event);
    if (!set) this.handlers.set(event, (set = new Set()));
    set.add(h);
    this.connection?.on(event, h);
    void this.connect().catch(() => undefined);
    return () => {
      set?.delete(h);
      this.connection?.off(event, h);
    };
  }

  private async join(group: GroupKey) {
    this.groups.add(group);
    const conn = this.connection;
    if (conn?.state === HubConnectionState.Connected) await this.invokeJoin(conn, group);
    else await this.connect().catch(() => undefined); // every group is joined as the connection starts
  }

  private async leave(group: GroupKey, method: string, id: string) {
    this.groups.delete(group);
    if (this.connection?.state === HubConnectionState.Connected) await this.connection.invoke(method, id).catch(() => undefined);
  }

  joinPolicy(policyId: string): Promise<void> {
    return this.join(`policy:${policyId}`);
  }

  leavePolicy(policyId: string): Promise<void> {
    return this.leave(`policy:${policyId}`, HUB_METHODS.leavePolicy, policyId);
  }

  joinRun(runId: string): Promise<void> {
    return this.join(`run:${runId}`);
  }

  leaveRun(runId: string): Promise<void> {
    return this.leave(`run:${runId}`, HUB_METHODS.leaveRun, runId);
  }

  async stop(): Promise<void> {
    this.groups.clear();
    await this.connection?.stop().catch(() => undefined);
    this.connection = null;
    this.setStatus("idle");
  }
}

const LiveContext = createContext<LiveClient | null>(null);

/** Provides one live connection per app; it connects once enabled and something subscribes. Stops when the user signs out. */
export function LiveProvider({ children, enabled = true }: { children: ReactNode; enabled?: boolean }) {
  const client = useMemo(() => new LiveClient(), []);
  useEffect(() => {
    client.setEnabled(enabled);
    return () => client.setEnabled(false);
  }, [client, enabled]);
  return <LiveContext.Provider value={client}>{children}</LiveContext.Provider>;
}

export function useLive(): LiveClient {
  const ctx = useContext(LiveContext);
  if (!ctx) throw new Error("useLive must be used inside <LiveProvider>");
  return ctx;
}

export function useLiveStatus(): LiveStatus {
  const live = useLive();
  const [status, setStatus] = useState(live.getStatus());
  useEffect(() => live.onStatus(setStatus), [live]);
  return status;
}

/** Subscribes to a server event for the component's lifetime; the latest handler is always called. */
export function useLiveEvent<T = unknown>(event: string, handler: (payload: T) => void, enabled = true): void {
  const live = useLive();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!enabled) return;
    return live.subscribe<T>(event, (p) => ref.current(p));
  }, [live, event, enabled]);
}

/** Joins the policy group while mounted (policy workspace). */
export function usePolicyChannel(policyId: string | undefined): void {
  const live = useLive();
  useEffect(() => {
    if (!policyId) return;
    void live.joinPolicy(policyId);
    return () => void live.leavePolicy(policyId);
  }, [live, policyId]);
}

/** Joins an agent run's group while mounted (run timeline). */
export function useRunChannel(runId: string | undefined): void {
  const live = useLive();
  useEffect(() => {
    if (!runId) return;
    void live.joinRun(runId);
    return () => void live.leaveRun(runId);
  }, [live, runId]);
}
